/**
 * useNavigationSettings Hook
 *
 * Single responsibility: Connect components to navigation settings store
 * Returns settings and actions to modify them
 * Easy to use in any component that needs navigation config
 */

import { useCallback } from 'react'
import { NavigationSettings } from '../types/NavigationSettings'
import { useNavigationSettingsStore } from '../store/navigationSettingsSlice'

/**
 * Hook to access navigation settings and actions
 *
 * Usage:
 * ```tsx
 * const { settings, updateSetting, reset } = useNavigationSettings()
 *
 * // Read settings
 * console.log(settings.distanceThresholdPercent)
 *
 * // Update single setting
 * updateSetting('distanceThresholdPercent', 75)
 *
 * // Update multiple settings
 * updateSettings({ useConnectivityFiltering: false, max3DDistance: 2.5 })
 *
 * // Reset to defaults
 * reset()
 * ```
 */
export function useNavigationSettings() {
  // Get state from store
  const settings = useNavigationSettingsStore((state) => state.settings)
  const isLoading = useNavigationSettingsStore((state) => state.isLoading)
  const error = useNavigationSettingsStore((state) => state.error)

  // Get actions from store
  const setSettings = useNavigationSettingsStore((state) => state.setSettings)
  const updateSingleSetting = useNavigationSettingsStore((state) => state.updateSingleSetting)
  const resetSettings = useNavigationSettingsStore((state) => state.resetSettings)
  const saveSettings = useNavigationSettingsStore((state) => state.saveSettings)
  const loadSettings = useNavigationSettingsStore((state) => state.loadSettings)

  /**
   * Update multiple settings at once (partial update)
   */
  const updateSettings = useCallback(
    (newSettings: Partial<NavigationSettings>) => {
      setSettings(newSettings)
      // Auto-save after update
      saveSettings().catch(console.error)
    },
    [setSettings, saveSettings]
  )

  /**
   * Update a single setting by key
   */
  const updateSetting = useCallback(
    <K extends keyof NavigationSettings>(key: K, value: NavigationSettings[K]) => {
      updateSingleSetting(key, value)
      // Auto-save after update
      saveSettings().catch(console.error)
    },
    [updateSingleSetting, saveSettings]
  )

  /**
   * Reset to default settings
   */
  const reset = useCallback(() => {
    resetSettings()
    // Auto-save reset
    saveSettings().catch(console.error)
  }, [resetSettings, saveSettings])

  return {
    // State
    settings,
    isLoading,
    error,

    // Actions
    updateSettings,      // Update multiple settings
    updateSetting,       // Update single setting
    reset,               // Reset to defaults
    saveSettings,        // Manual save to localStorage
    loadSettings,        // Manual load from localStorage
  }
}

/**
 * Hook to access specific settings without unnecessary re-renders
 * Use this when you only need a few settings values
 *
 * Usage:
 * ```tsx
 * const distanceThreshold = useNavigationSetting('distanceThresholdPercent')
 * const connectivity = useNavigationSetting('useConnectivityFiltering')
 * ```
 */
export function useNavigationSetting<K extends keyof NavigationSettings>(
  key: K
): NavigationSettings[K] {
  return useNavigationSettingsStore((state) => state.settings[key])
}

/**
 * Hook to get all navigation actions without reacting to settings changes
 * Useful for components that don't display settings but need to modify them
 *
 * Usage:
 * ```tsx
 * const { updateSetting, reset } = useNavigationSettingsActions()
 * ```
 */
export function useNavigationSettingsActions() {
  const updateSingleSetting = useNavigationSettingsStore((state) => state.updateSingleSetting)
  const resetSettings = useNavigationSettingsStore((state) => state.resetSettings)
  const saveSettings = useNavigationSettingsStore((state) => state.saveSettings)

  const updateSetting = useCallback(
    <K extends keyof NavigationSettings>(key: K, value: NavigationSettings[K]) => {
      updateSingleSetting(key, value)
      saveSettings().catch(console.error)
    },
    [updateSingleSetting, saveSettings]
  )

  const reset = useCallback(() => {
    resetSettings()
    saveSettings().catch(console.error)
  }, [resetSettings, saveSettings])

  return { updateSetting, reset, saveSettings }
}
